import { supabase } from './supabase/client';
import { invokeAdminUsers } from './supabase/adminUsers';
import {
  Teacher,
  CreateTeacherDTO,
  UpdateTeacherDTO,
  TeacherFilters,
  TeacherListResponse,
  Badge,
  CreateBadgeDTO,
  UpdateBadgeDTO,
  BadgeFilters,
  BadgeListResponse,
} from 'src/types/teacher.types';

const TABLE_NAME = 'teachers';
const BADGES_TABLE = 'badges';

/**
 * Service for managing teacher data
 */
export const teacherService = {
  /**
   * Get teachers with filters and pagination
   */
  async getTeachers({ subject, search, page = 1, limit = 10 }: TeacherFilters = {}): Promise<TeacherListResponse> {
    try {
      let query = supabase.from(TABLE_NAME).select('*, user:users!inner(*)', { count: 'exact' });

      query = query.is('user.deletedat', null);

      if (subject) {
        query = query.eq('subject', subject);
      }

      if (search) {
        query = query.or(
          `and(user.firstname.ilike.%${search}%,user.lastname.ilike.%${search}%),user.email.ilike.%${search}%`
        );
      }

      // Add pagination
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      query = query.range(from, to).order('createdat', { ascending: false });

      const { data, error, count } = await query;

      if (error) {
        console.error('Query error:', error);
        throw error;
      }

      return {
        data: data as Teacher[],
        total: count || 0,
        page,
        limit,
      };
    } catch (error) {
      console.error('[TeacherService.getTeachers]:', error);
      throw error;
    }
  },

  /**
   * Get a single teacher by ID (supports teacher table ID or user ID)
   */
  async getTeacherById(id: string): Promise<Teacher | null> {
    try {
      let { data, error } = await supabase
        .from(TABLE_NAME)
        .select('*, user:users!inner(*)')
        .eq('id', id)
        .maybeSingle();

      if (!data) {
        const fallbackRes = await supabase
          .from(TABLE_NAME)
          .select('*, user:users!inner(*)')
          .eq('userid', id)
          .maybeSingle();
        data = fallbackRes.data;
      }

      return data;
    } catch (error) {
      console.error('[TeacherService.getTeacherById]:', error);
      throw error;
    }
  },

  /**
   * Create a new teacher with Supabase auth account
   */
  async createTeacher(teacher: CreateTeacherDTO): Promise<Teacher> {
    try {
      // The Edge Function creates the login, users row and teachers row, and undoes them on failure.
      return await invokeAdminUsers<Teacher>('create_teacher', {
        email: teacher.email,
        password: teacher.password,
        firstname: teacher.firstname,
        lastname: teacher.lastname,
        extendedproperties: teacher.extendedproperties,
      });
    } catch (error) {
      console.error('[TeacherService.createTeacher]:', error);
      throw error;
    }
  },

  /**
   * Update an existing teacher
   */
  async updateTeacher(id: string, updates: UpdateTeacherDTO): Promise<Teacher> {
    try {
      const teacher = await this.getTeacherById(id);
      if (!teacher) throw new Error('Teacher not found');

      if (teacher.user.email !== updates.email) {
        await invokeAdminUsers('update_email', { userId: id, email: updates.email });
      }

      // Update user data
      const { error: userError } = await supabase
        .from('users')
        .update({
          firstname: updates.firstname,
          lastname: updates.lastname,
          email: updates.email,
          phone: updates.phone,
        })
        .eq('id', id);

      if (userError) throw userError;

      // Update teacher data
      const { data: teacherData, error: teacherError } = await supabase
        .from(TABLE_NAME)
        .update({
          extendedproperties: {
            ...teacher.extendedproperties,
            ...updates.extendedproperties,
          },
        })
        .eq('userid', id);

      if (teacherError) throw teacherError;

      return teacherData;
    } catch (error) {
      console.error('[TeacherService.updateTeacher]:', error);
      throw error;
    }
  },

  /**
   * Delete a teacher
   */
  async deleteTeacher(id: string): Promise<void> {
    try {
      // Removes the teachers row, users row and login together.
      await invokeAdminUsers('delete_teacher', { userId: id });
    } catch (error) {
      console.error('[TeacherService.deleteTeacher]:', error);
      throw error;
    }
  },
};
